/**
 * useProcessedCollection Hook
 *
 * Processes a GeoJSON collection through multiple stages:
 * 1. Apply filters
 * 2. Remove excluded features
 * 3. Auto-merge based on prompt keys (optional)
 *
 * This hook provides a single, memoized processing pipeline
 * for both Quiz Studio preview and Quiz Play.
 */

import { useMemo } from "react";
import type { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import type { FilterRule } from "@/stores/useStudioStore";
import { applyFilters } from "@/utils/filterUtils";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";
import { autoMerge } from "@/utils/grassyJSON";

interface UseProcessedCollectionOptions {
  /** Enable auto-merge based on prompt keys */
  enableAutoMerge?: boolean;
  /** Buffer distance for merge operations (meters) - default 200m for simplified GeoJSON files */
  bufferDistance?: number;
  /** Coordinate precision (decimal places) */
  precision?: number;
}

/**
 * Process a GeoJSON collection with filters, exclusions, and optional auto-merge
 */
export function useProcessedCollection(
  collection: FeatureCollection<Geometry, GeoJsonProperties> | null,
  filters: FilterRule[],
  excludedFeatureIds: string[],
  promptKeys: string[],
  options: UseProcessedCollectionOptions = {}
): FeatureCollection<Geometry, GeoJsonProperties> | null {
  const {
    enableAutoMerge = true,
    bufferDistance = 200,
    precision = 6,
  } = options;

  return useMemo(() => {
    if (!collection) return null;

    // Step 1: Apply filters
    let processed = applyFilters(collection, filters);

    // Step 2: Remove excluded features
    if (excludedFeatureIds.length > 0) {
      const excludedSet = new Set(excludedFeatureIds);
      processed = {
        ...processed,
        features: processed.features.filter((feature, index) => {
          const featureId = getFeatureIdentifier(feature, index);
          return !excludedSet.has(featureId);
        }),
      };
    }

    // Step 3: Auto-merge based on prompt keys
    if (enableAutoMerge && promptKeys.length > 0) {
      // Build comparison function from prompt keys
      // Features match if ALL prompt key values are equal
      const comparisonFunc = (
        a: { properties?: GeoJsonProperties | null },
        b: { properties?: GeoJsonProperties | null }
      ): boolean => {
        // Both must have properties
        if (!a.properties || !b.properties) return false;

        // Check ALL prompt keys match
        return promptKeys.every((key) => {
          const aValue = a.properties?.[key];
          const bValue = b.properties?.[key];

          // Handle arrays by converting to sorted strings for comparison
          const aStr = Array.isArray(aValue)
            ? JSON.stringify([...aValue].sort())
            : JSON.stringify(aValue);
          const bStr = Array.isArray(bValue)
            ? JSON.stringify([...bValue].sort())
            : JSON.stringify(bValue);

          return aStr === bStr;
        });
      };

      // Apply auto-merge
      try {
        processed = autoMerge(processed, comparisonFunc, bufferDistance, precision);
      } catch (error) {
        console.error("Auto-merge failed:", error);
        // Fall back to non-merged collection on error
      }
    }

    return processed;
  }, [collection, filters, excludedFeatureIds, promptKeys, enableAutoMerge, bufferDistance, precision]);
}

/**
 * Extract the prompt keys comparison string for debugging
 */
export function getPromptKeysLabel(promptKeys: string[]): string {
  if (promptKeys.length === 0) return "";
  if (promptKeys.length === 1) return promptKeys[0]!;
  return promptKeys.join(" + ");
}
